import { assign, fromPromise, setup } from 'xstate';
import { createBaseCrudMachine } from '~/machines/base-crud';
import { ProviderPrivilege } from '~/types';
import { showAxiosErrorAction } from '../../actions';
import {
  addPrivilegesToCrudContext,
  creatingCustom,
  openBulkDeleteConfirmation,
  openConfirmation,
  removePrivilegesFromCrudContext,
  resetCreatingCustom,
  resetLoading,
  sendContextToCrud,
  sendLoadToCrud,
  setAvailablePrivileges,
  setIdsToDelete,
  setLoading,
} from './actions';
import { API_CALLS, BULK_API_CALLS, downloadPrivilegesCsv, getAvailablePrivileges } from './api';
import { getEmptyPrivilegesForm } from './constants';
import { PrivilegeMachineEvents as Events, OnSendSharedContextEvent } from './events';
import { PrivilegeMachineContext as Context, PrivilegesFormValues, PrivilegesSort } from './types';

export const privilegeCrudMachine = createBaseCrudMachine<
  ProviderPrivilege,
  PrivilegesFormValues,
  PrivilegesSort
>({
  initialForm: getEmptyPrivilegesForm(),
  sort: {
    sortBy: 'specialty',
    specialty_name: -1,
    category: -1,
    privilege_type: -1,
    privilege_name: -1,
    assignment_date: -1,
    id: 1,
    page: 1,
    pageSize: 100,
  },
  apiCalls: API_CALLS,
});

export const privilegesMachine = setup({
  actors: {
    crudRef: privilegeCrudMachine,
    downloadCsv: fromPromise(downloadPrivilegesCsv),
    getAvailablePrivileges: fromPromise(getAvailablePrivileges),
    deleteMultiple: fromPromise(BULK_API_CALLS.delete),
    createMultiple: fromPromise(BULK_API_CALLS.create),
  },
  actions: {
    showError: showAxiosErrorAction,
    setProviderIds: assign({
      providerId: ({ event }: { event: Events }) =>
        (event as OnSendSharedContextEvent).values.providerId,
      providerCategoryId: ({ event }: { event: Events }) =>
        (event as OnSendSharedContextEvent).values.providerCategoryId,
    }),
    sendContextToCrud: sendContextToCrud,
    sendLoadToCrud: sendLoadToCrud,
    setAvailablePrivileges: assign(setAvailablePrivileges),
    setLoading: assign(setLoading),
    resetLoading: assign(resetLoading),
    setIdsToDelete: assign(setIdsToDelete),
    setCreatingCustom: assign(creatingCustom),
    resetCreatingCustom: assign(resetCreatingCustom),
    removePrivilegesFromCrudContext: removePrivilegesFromCrudContext,
    addPrivilegesToCrudContext: addPrivilegesToCrudContext,
    openConfirmation: openConfirmation,
    openBulkDeleteConfirmation: openBulkDeleteConfirmation,
  },
  types: {
    context: {} as Context,
    events: {} as Events,
    input: {} as Partial<Context>,
  },
}).createMachine({
  context: ({ spawn }) => ({
    crudRef: spawn('crudRef', {
      id: 'crudState',
      input: { data: [] },
    }),
    idsToDelete: [],
    creatingCustom: false,
    availablePrivileges: [],
    providerId: -1,
    providerCategoryId: -1,
    loading: false,
    pagination: {
      page: 1,
      pageSize: 100,
      total: 0,
    },
  }),
  initial: '$_IDLE',
  // Always-handled events so page navigation (ON_LOAD), provider context updates,
  // and bulk create/delete requests aren't silently dropped while the machine is
  // in a transient invoke state (e.g. mid-$_GET_AVAILABLE_PRIVILEGES).
  on: {
    ON_SEND_SHARED_CONTEXT: {
      actions: ['setProviderIds', 'sendContextToCrud'],
    },
    ON_LOAD: {
      actions: ['sendLoadToCrud'],
    },
    // Guard on `!loading` so a rapid double-fire (e.g. double-click on "Add
    // Selected") can't re-enter the invoke state mid-flight and submit the
    // same payload twice. `loading` is set on entry and cleared on
    // done/error, so it tracks in-flight bulk work exactly.
    ON_CREATE_MULTIPLE: {
      guard: ({ context }) => !context.loading,
      actions: ['setLoading'],
      target: '.$_CREATE_MULTIPLE',
    },
    ON_CONFIRMED_DELETE_MULTIPLE: {
      guard: ({ context }) => !context.loading,
      target: '.$_DELETE_MULTIPLE',
      actions: ['setIdsToDelete', 'setLoading'],
    },
  },
  states: {
    $_IDLE: {
      on: {
        ON_DOWNLOAD: {
          actions: ['setLoading'],
          target: '$_DOWNLOAD',
        },
        ON_GET_AVAILABLE_PRIVILEGES: {
          target: '$_GET_AVAILABLE_PRIVILEGES',
          actions: ['setLoading'],
        },
        ON_SEND_DELETE_CONFIRMATION: {
          actions: ['openConfirmation'],
        },
        ON_DELETE_MULTIPLE: {
          actions: ['setIdsToDelete', 'openBulkDeleteConfirmation'],
        },
        ON_CREATE_CUSTOM: {
          target: '$_CREATE_CUSTOM',
          actions: ['setCreatingCustom'],
        },
      },
    },
    $_DOWNLOAD: {
      target: '$_IDLE',
      invoke: {
        id: 'download',
        src: 'downloadCsv',
        input: ({ context, event }) => ({ context, event }) as { context: Context; event: Events },
        onDone: {
          target: '$_IDLE',
          actions: ['resetLoading'],
        },
        onError: {
          target: '$_IDLE',
          actions: ['showError', 'resetLoading'],
        },
      },
    },
    $_DELETE_MULTIPLE: {
      invoke: {
        id: 'deleteMultiple',
        src: 'deleteMultiple',
        input: ({ context, event }) => ({ context, event }) as { context: Context; event: Events },
        onDone: {
          target: '$_IDLE',
          actions: ['resetLoading', 'removePrivilegesFromCrudContext'],
        },
        onError: {
          target: '$_IDLE',
          actions: ['showError', 'resetLoading'],
        },
      },
    },
    $_CREATE_MULTIPLE: {
      invoke: {
        id: 'createMultiple',
        src: 'createMultiple',
        input: ({ context, event }) => ({ context, event }) as { context: Context; event: Events },
        onDone: {
          // Bulk create can partially fail; sendLoadToCrud refetches so optimistic append can't desync the grid.
          target: '$_IDLE',
          actions: ['resetLoading', 'addPrivilegesToCrudContext', 'sendLoadToCrud'],
        },
        onError: {
          target: '$_IDLE',
          actions: ['showError', 'resetLoading'],
        },
      },
    },
    $_GET_AVAILABLE_PRIVILEGES: {
      invoke: {
        id: 'getAvailablePrivileges',
        src: 'getAvailablePrivileges',
        input: ({ context, event }) => ({ context, event }) as { context: Context; event: Events },
        onDone: {
          target: '$_IDLE',
          actions: ['setAvailablePrivileges', 'resetLoading'],
        },
        onError: {
          target: '$_IDLE',
          actions: ['showError', 'resetLoading'],
        },
      },
    },
    $_CREATE_CUSTOM: {
      on: {
        ON_CLOSE_MODAL: {
          target: '$_IDLE',
          actions: ['resetCreatingCustom'],
        },
      },
    },
  },
});
